/**
 * Styled option list for the product page's variant dropdowns.
 *
 * A native <select> cannot have its open list styled — the browser draws it —
 * so this element paints its own list on top while the real <select> stays in
 * the DOM, hidden but functional. Every choice is written back to the select
 * and announced with a bubbling `change`, which is what the theme's variant
 * logic listens for, so nothing downstream needs to know this exists.
 *
 * Touch devices keep the native list: the iOS/Android pickers are easier to
 * operate with a thumb than any list rendered inside the page.
 *
 * The element is re-created whenever the section re-renders after a variant
 * change, so connectedCallback is the only entry point.
 */
if (!customElements.get('variant-select-list')) {
  class VariantSelectList extends HTMLElement {
    connectedCallback() {
      if (!this.querySelector('select')) return;
      if (!window.matchMedia('(pointer: fine)').matches) return;

      this.rebuild();

      /*
       * Picking a size makes the theme re-render the picker, and it morphs the
       * existing DOM instead of replacing this element — so connectedCallback
       * never fires again and the morph wipes the button and list added here.
       * Watching for that and rebuilding is what keeps the control alive past
       * the first choice.
       */
      this.observer = new MutationObserver(() => {
        if (!this.querySelector('.variant-select-list__button')) this.rebuild();
      });
      this.observer.observe(this, { childList: true });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('click', this.onDocumentClick);
    }

    /** Build without the observer reacting to our own DOM writes. */
    rebuild() {
      this.observer?.disconnect();
      this.build();
      if (this.observer) this.observer.observe(this, { childList: true });
    }

    build() {
      this.select = this.querySelector('select');
      if (!this.select) return;

      this.querySelectorAll('.variant-select-list__button, .variant-select-list__list').forEach((el) => el.remove());
      document.removeEventListener('click', this.onDocumentClick);

      this.options = Array.from(this.select.options);

      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'variant-select-list__button';
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.innerHTML = `<span class="variant-select-list__value"></span>`;

      this.list = document.createElement('ul');
      this.list.className = 'variant-select-list__list';
      this.list.setAttribute('role', 'listbox');
      this.list.hidden = true;

      this.options.forEach((option, index) => {
        const item = document.createElement('li');
        item.className = 'variant-select-list__item';
        item.setAttribute('role', 'option');
        item.dataset.index = String(index);
        item.textContent = option.textContent.trim();
        if (option.disabled) item.setAttribute('aria-disabled', 'true');
        item.addEventListener('click', () => this.choose(index));
        this.list.append(item);
      });

      this.append(this.button, this.list);
      this.sync();

      this.button.addEventListener('click', () => this.toggle());
      this.button.addEventListener('keydown', (event) => this.onKeydown(event));
      this.list.addEventListener('keydown', (event) => this.onKeydown(event));

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      document.addEventListener('click', this.onDocumentClick);

      this.setAttribute('data-enhanced', 'true');
    }

    /** Mirror the select's current value onto the button and the list. */
    sync() {
      const index = this.select.selectedIndex;
      this.querySelector('.variant-select-list__value').textContent =
        this.options[index]?.textContent.trim() ?? '';

      Array.from(this.list.children).forEach((item, i) => {
        item.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    choose(index) {
      const option = this.options[index];
      if (!option || option.disabled) return;

      this.select.selectedIndex = index;
      this.sync();
      this.close();
      this.button.focus();
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    toggle() {
      this.list.hidden ? this.open() : this.close();
    }

    open() {
      this.list.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');

      const selected = this.list.querySelector('[aria-selected="true"]');
      if (selected) selected.scrollIntoView({ block: 'nearest' });
      this.active = this.select.selectedIndex;
      this.highlight(this.active);
    }

    close() {
      this.list.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      this.list.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
    }

    highlight(index) {
      Array.from(this.list.children).forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
        if (i === index) item.scrollIntoView({ block: 'nearest' });
      });
    }

    onKeydown(event) {
      const aberto = !this.list.hidden;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          event.preventDefault();
          if (!aberto) return this.open();
          const passo = event.key === 'ArrowDown' ? 1 : -1;
          this.active = Math.min(this.options.length - 1, Math.max(0, (this.active ?? 0) + passo));
          this.highlight(this.active);
          break;
        }
        case 'Enter':
        case ' ':
          event.preventDefault();
          aberto ? this.choose(this.active ?? this.select.selectedIndex) : this.open();
          break;
        case 'Escape':
          if (aberto) {
            event.preventDefault();
            this.close();
            this.button.focus();
          }
          break;
        case 'Tab':
          this.close();
          break;
      }
    }
  }

  customElements.define('variant-select-list', VariantSelectList);
}
